export const STUDIO_MODULE_IDS = ["CATALOGUE", "GEO", "ORDERS", "CAPTURE", "AI", "KARKHANA", "CARE", "BILLING_SYNC", "CASHBOOK"] as const;
export type StudioModuleId = (typeof STUDIO_MODULE_IDS)[number];

const TRIAL_ELIGIBLE: StudioModuleId[] = ["CATALOGUE", "GEO", "ORDERS", "CAPTURE", "AI", "KARKHANA"];

export const readAuthUser = (): any => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null");
  } catch {
    return null;
  }
};

export const hasEntitlementPayload = (authUser: any): boolean => {
  const org = authUser?.users?.organizationResponse;
  if (!org) {
    return false;
  }
  if (org.trialEndsAt || org.trialStartedAt) {
    return true;
  }
  if (typeof org.entitlementsEpoch === "number" && org.entitlementsEpoch > 0) {
    return true;
  }
  if (Array.isArray(org.moduleGrants) && org.moduleGrants.length > 0) {
    return true;
  }
  if (Array.isArray(org.enabledModules) && org.enabledModules.length > 0) {
    return true;
  }
  return false;
};

export const isStudioSuperAdmin = (authUser: any): boolean =>
  authUser?.users?.role === "SUPER_ADMIN";

export const trialEndsAtMs = (authUser: any): number => {
  const raw =
    authUser?.users?.organizationResponse?.trialEndsAt || authUser?.trialEndsAt;
  if (!raw) {
    return 0;
  }
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

export const isStudioTrialActive = (authUser: any = readAuthUser()): boolean =>
  trialEndsAtMs(authUser) > Date.now();

export const studioTrialDaysLeft = (authUser: any = readAuthUser()): number => {
  const ends = trialEndsAtMs(authUser);
  if (!ends) {
    return 0;
  }
  const diff = ends - Date.now();
  if (diff <= 0) {
    return 0;
  }
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const effectiveStudioModules = (authUser: any = readAuthUser()): Set<string> => {
  const ids = new Set<string>();
  const org = authUser?.users?.organizationResponse;
  if (isStudioTrialActive(authUser)) {
    TRIAL_ELIGIBLE.forEach((id) => ids.add(id));
  }
  const grants = org?.moduleGrants || [];
  const now = Date.now();
  grants.forEach((grant: any) => {
    // Org-trial grants carry no weight on their own; a module trial started later (Karkhana) has an end.
    if (!grant?.module || (grant.source === "TRIAL" && !grant.endsAt)) {
      return;
    }
    if (grant.endsAt && new Date(grant.endsAt).getTime() <= now) {
      return;
    }
    ids.add(String(grant.module).toUpperCase());
  });
  const optedOut = org?.optedOutModules || [];
  optedOut.forEach((raw: any) => {
    ids.delete(String(raw).toUpperCase());
  });
  return ids;
};

export const isStudioModuleEntitled = (
  moduleId: StudioModuleId,
  authUser: any = readAuthUser()
): boolean => {
  if (isStudioSuperAdmin(authUser)) {
    return true;
  }
  if (!hasEntitlementPayload(authUser)) {
    return true;
  }
  return effectiveStudioModules(authUser).has(moduleId);
};

/** The server says this studio may still start a free trial of the module (Karkhana on first open). */
export const isStudioModuleTrialAvailable = (
  moduleId: StudioModuleId,
  authUser: any = readAuthUser()
): boolean => {
  if (isStudioSuperAdmin(authUser)) {
    return false;
  }
  const available = authUser?.users?.organizationResponse?.moduleTrialsAvailable;
  return Array.isArray(available) && available.some((id: any) => String(id).toUpperCase() === moduleId);
};

/** End of a module's own running trial (not the org trial), or 0. */
export const moduleTrialEndsAtMs = (moduleId: StudioModuleId, authUser: any = readAuthUser()): number => {
  const grants = authUser?.users?.organizationResponse?.moduleGrants || [];
  const now = Date.now();
  let latest = 0;
  grants.forEach((grant: any) => {
    if (String(grant?.module || "").toUpperCase() !== moduleId || grant.source !== "TRIAL" || !grant.endsAt) {
      return;
    }
    const ends = new Date(grant.endsAt).getTime();
    if (!Number.isNaN(ends) && ends > now && ends > latest) {
      latest = ends;
    }
  });
  return latest;
};
